import styled from "styled-components";
import {Table} from "antd";
import {invoiceTheme} from "./invoiceTheme";

// Renkler yalnızca invoiceTheme'den okunur; $variant "cod" | "service"
const variantColor = key => props => invoiceTheme[props.$variant]?.[key];

export const PageTitle = styled.h2`
    margin-bottom: 16px;
    font-weight: 600;
`;

// $tone: invoiceTheme.stats içindeki anahtar
const statColor = key => props => invoiceTheme.stats[props.$tone]?.[key];

export const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin: 20px 0;

    @media (max-width: 768px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

export const StatCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    min-width: 0;
    padding: 14px;
    text-align: center;
    background: ${invoiceTheme.statCardBackground};
    border: 1px solid ${invoiceTheme.statCardBorder};
    border-radius: 8px;
`;

export const StatIcon = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${statColor("iconBackground")};
    color: ${statColor("icon")};
    font-size: 22px;
`;

// Kartın tutarına ait fatura sayısı ikonun sağ üst köşesinde gösterilir
export const StatBadge = styled.span`
    position: absolute;
    top: -8px;
    right: -10px;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 11px;
    border: 1px solid ${invoiceTheme.statBadgeBorder};
    background: ${invoiceTheme.statBadgeBackground};
    color: ${invoiceTheme.statBadgeText};
    font-size: 11px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
`;

export const StatValue = styled.div`
    max-width: 100%;
    overflow: hidden;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: ${invoiceTheme.statValue};
`;

// Uzun başlıklar dar ekranda en fazla 2 satır; daha da daralırsa "…" ve title tooltip devreye girer
export const StatLabel = styled.div`
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    min-height: calc(2 * 1.35em);
    color: ${invoiceTheme.statLabel};
    font-size: 12px;
    font-weight: 500;
    line-height: 1.35;
`;

export const MainTableWrapper = styled.div`
    .ant-table-expanded-row > td {
        background: transparent !important;
        padding: 0 !important;
    }

    .ant-table-expanded-row > td > .ant-table-expanded-row-fixed {
        padding: 0 !important;
        margin: 0 !important;
    }
`;

const rowColor = key => invoiceTheme.subCompanyRows[key];

export const SubCompanyRows = styled.div`
    background: ${rowColor("background")};
`;

export const SubCompanyRowWrapper = styled.div`
    padding: 12px;

    & + & {
        padding-top: 0;
    }
`;

export const SubCompanyRowHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    cursor: pointer;
    user-select: none;
    background: ${rowColor("rowBackground")};
    transition: background-color 0.2s;

    &:hover {
        background: ${rowColor("rowHoverBackground")};
    }

    @media screen and (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

export const SubCompanyIcon = styled.div`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: ${rowColor("iconBackground")};
    color: ${rowColor("icon")};
    font-size: 16px;
`;

export const SubCompanyFields = styled.div`
    display: grid;
    flex: 1;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    min-width: 0;

    @media screen and (max-width: 768px) {
        grid-template-columns: minmax(0, 1fr);
        width: 100%;
    }
`;

export const SubCompanyField = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;

    .label {
        color: ${rowColor("label")};
        font-size: 12px;
    }

    .value {
        overflow: hidden;
        color: ${rowColor("value")};
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
`;

export const SubCompanyTotal = styled.div`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 8px;

    .label,
    .count {
        color: ${rowColor("label")};
        font-size: 13px;
    }

    .amount {
        color: ${rowColor("value")};
        font-size: 14px;
        font-weight: 600;
    }

    .arrow {
        margin-left: 8px;
        color: ${rowColor("arrow")};
        font-size: 12px;
    }
`;

export const SubCompanyRowContent = styled.div`
    padding: 12px;
    background: ${invoiceTheme.expandedRowBackground};
`;

export const ExpandedWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

export const GroupBox = styled.div`
    border: 1px solid ${variantColor("outerBorder")};
    background: ${variantColor("outerBackground")};
    box-shadow: 0 2px 8px ${variantColor("outerShadow")};
    overflow: hidden;
`;

export const GroupHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    cursor: pointer;
    user-select: none;
    background: ${variantColor("headerBackground")};
    border-bottom: ${props =>
        props.$expanded ? `1px solid ${variantColor("headerBorder")(props)}` : "none"};
    color: ${variantColor("headerText")};

    .group-icon {
        font-size: 18px;
    }

    .group-title {
        flex: 1;
        font-size: 15px;
        font-weight: 500;
    }

    .group-count {
        color: ${variantColor("countText")};
    }
`;

export const GroupContent = styled.div`
    padding: 12px;
`;

export const InnerTable = styled(Table)`
    .ant-table {
        background: transparent;
    }

    .ant-table-thead > tr > th {
        background: ${variantColor("tableHeaderBackground")};
        color: ${variantColor("tableHeaderText")};
        font-weight: 500;
    }

    .ant-table-tbody > tr > td {
        background: ${variantColor("tableRowBackground")};
        border-bottom: 1px solid ${variantColor("tableRowBorder")};
    }

    .ant-table-tbody > tr:hover > td {
        background: ${variantColor("tableRowHoverBackground")};
    }
`;

export const DetailIconButton = styled.span`
    cursor: pointer;
    font-size: 18px;
    color: ${invoiceTheme.detailIcon};

    &:hover {
        color: ${invoiceTheme.detailIconHover};
    }
`;

export const EmptyText = styled.div`
    padding: 12px;
    color: #8c8c8c;
`;
